import { setReduxDataTypeIntroTour } from "../Redux/futures/home/actions";
import {
  KEY_LOCAL_TOUR_COMPLETE,
  KEY_LOCAL_TYPE_USER,
  TYPE_USER,
} from "../helper/const";

// ═══════════════════════════════════════════════════════════════════════════
// Khởi tạo state guide tour ngay sau khi đăng nhập.
//
// Logic này vốn được copy-paste 4 lần trong ModalLogin.jsx (login password +
// OTP) và LoginMobile.jsx (login password + OTP), nhưng LayoutApp.jsx — luồng
// AUTO-LOGIN của WebView Mytel — thì bị bỏ sót: nó chỉ ghi localStorage
// KEY_LOCAL_TYPE_USER mà không dispatch gì vào redux.
//
// Hậu quả: reducer home lấy typeActive từ localStorage ở initState, mà initState
// chạy lúc TẠO STORE — trước khi auto-login xong. Lúc đó localStorage còn rỗng
// nên typeActive = TYPE_USER.general, và không có dispatch nào sửa lại
// ⇒ popup mời xem hướng dẫn (pu4) KHÔNG BAO GIỜ hiện trên luồng auto-login.
//
// Tách ra đây để LayoutApp dùng chung một bản đúng. 4 chỗ copy-paste cũ vẫn giữ
// nguyên (không đụng để tránh rủi ro hồi quy) — nên gộp dần về hàm này.
// ═══════════════════════════════════════════════════════════════════════════

// Các nhóm user được đặt lại về màn mời (step 0) ở mỗi lần đăng nhập. Popup mời
// chỉ có nội dung cho TOUR_INVITE_TYPES; tần suất lặp lại do isTourInviteDue quyết định.
const PU4_ELIGIBLE_TYPES = [
  TYPE_USER.newNoPackage,
  TYPE_USER.newPackage,
  TYPE_USER.firstLoginAfterRegistered7Days,
  TYPE_USER.firstLoginWithinRegistered2To6Days,
];

/**
 * Tour hướng dẫn (joyride) có đang chạy không.
 *
 * Quy ước `step` trong redux typeIntroTour:
 *   0      — đang ở màn mời bắt đầu (NewUserTourInvite), tour CHƯA chạy
 *   1..n   — tour ĐANG chạy, các bước highlight đang hiển thị trên trang
 *   -1     — tour đã xong hoặc bị bỏ qua
 *
 * Khi tour đang chạy, popup manager phải chặn TẤT CẢ popup — nguyên tắc "tại một
 * thời điểm chỉ có một thứ chiếm màn hình".
 */
export function isIntroTourRunning(typeIntroTour) {
  if (!typeIntroTour) return false;

  // KHÔNG chỉ nhìn `step`. Tour ở trang bid kết thúc qua handleNextEnd
  // (IntroTourBid.jsx:85-94): nó đặt KEY_LOCAL_TOUR_COMPLETE nhưng để nguyên
  // step = 4/5, không đưa về -1. Nếu chỉ xét step thì tour kẹt "đang chạy" vĩnh
  // viễn và chặn chết mọi popup cho tới khi user reload trang.
  if (localStorage.getItem(KEY_LOCAL_TOUR_COMPLETE) === "true") return false;

  return (
    +typeIntroTour.typeActive !== TYPE_USER.general &&
    +typeIntroTour.step >= 1
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Popup mời bắt đầu tour (trước đây là pu4 trong popup manager).
//
// Đây là LUỒNG DÀNH CHO NGƯỜI MỚI, KHÔNG thuộc popup manager: không có trong
// popupRules/popupRegistry, không tính vào "1 popup / lần vào", cooldown hay
// "3 popup / ngày". Component hiển thị: component/NewUserTourInvite.jsx.
//
// Popup manager chỉ nhường màn hình: khi popup mời đang chờ hoặc tour đang chạy
// (isNewUserFlowActive) thì nó hoãn lượt xét ở Home.
// ═══════════════════════════════════════════════════════════════════════════

// Nhóm user có nội dung popup mời (ComponentModalLayout chỉ render cho 2 nhóm này).
export const TOUR_INVITE_TYPES = [TYPE_USER.newNoPackage, TYPE_USER.newPackage];

// Đóng popup (chạm ra ngoài) thì 7 ngày sau mới mời lại.
const TOUR_INVITE_REPEAT_MS = 7 * 24 * 60 * 60 * 1000;
const KEY_TOUR_INVITE_DISMISSED_AT = "tour_invite_dismissed_at";
// Giữ nguyên tên key cũ (thời còn là pu4) để cờ user đã chọn không bị mất.
const KEY_TOUR_INVITE_NEVER_SHOW = "pm_pu4_never_show";

const neverShowKey = (userId) => `${KEY_TOUR_INVITE_NEVER_SHOW}_${userId}`;
const dismissedAtKey = (userId) => `${KEY_TOUR_INVITE_DISMISSED_AT}_${userId}`;

// "Không hiện lại" — ghi localStorage để có tác dụng ngay. Nguồn chuẩn trên server
// là WEB_USER.is_intro_dismissed (POST /front/user/dismiss-intro).
export function setNeverShowTourInvite(userId) {
  if (userId) localStorage.setItem(neverShowKey(userId), "true");
}

export function isNeverShowTourInvite(userId) {
  return !!userId && localStorage.getItem(neverShowKey(userId)) === "true";
}

// Gọi khi /current trả is_intro_dismissed = 0 — server là nguồn chuẩn.
export function clearNeverShowTourInvite(userId) {
  if (userId) localStorage.removeItem(neverShowKey(userId));
}

// User đóng popup mời mà không bắt đầu tour.
export function dismissTourInvite(userId, now = Date.now()) {
  if (userId) localStorage.setItem(dismissedAtKey(userId), String(now));
}

/**
 * Popup mời bắt đầu tour có cần hiện không (chưa xét trang đang đứng).
 *   - user đã đăng nhập, thuộc TOUR_INVITE_TYPES, đang ở màn mời (step 0)
 *   - tour chưa xong trên máy này
 *   - chưa chọn "không hiện lại" (server hoặc localStorage)
 *   - chưa đóng popup trong 7 ngày qua
 */
export function isTourInviteDue({ typeIntroTour, userInfo } = {}, now = Date.now()) {
  const userId = userInfo?.id;
  if (!userId) return false;
  if (+typeIntroTour?.step !== 0) return false;
  if (!TOUR_INVITE_TYPES.includes(+typeIntroTour?.typeActive)) return false;
  if (localStorage.getItem(KEY_LOCAL_TOUR_COMPLETE) === "true") return false;
  if (+userInfo.is_intro_dismissed === 1 || isNeverShowTourInvite(userId)) return false;

  const dismissedAt = parseInt(localStorage.getItem(dismissedAtKey(userId)), 10);
  if (dismissedAt && now - dismissedAt < TOUR_INVITE_REPEAT_MS) return false;

  return true;
}

/**
 * Luồng người mới đang chiếm màn hình: popup mời đang chờ user chọn, hoặc tour
 * đang chạy. Popup manager dùng hàm này để nhường chỗ.
 */
export function isNewUserFlowActive({ typeIntroTour, userInfo } = {}) {
  return isIntroTourRunning(typeIntroTour) || isTourInviteDue({ typeIntroTour, userInfo });
}

function readTourHistory(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch (_) {
    // Giá trị hỏng thì coi như chưa từng show, đừng để ném lỗi chặn cả luồng login.
    return {};
  }
}

/**
 * Ghi KEY_LOCAL_TYPE_USER và dispatch typeIntroTour tương ứng.
 *
 * @param {object}   params
 * @param {number|string} params.typeUser    - type_user từ response đăng nhập
 * @param {string}   params.phoneNumber      - dùng làm khoá TOUR_SHOWN_<sđt>
 * @param {function} params.dispatch         - redux dispatch
 */
export function initIntroTourAfterLogin({ typeUser, phoneNumber, dispatch }) {
  // KHÔNG dùng `!typeUser`: TYPE_USER.general = 0 là falsy, sẽ return sớm và bỏ
  // qua nhánh đánh dấu "tour đã xong" — khiến isIntroTourCompleted() mãi false
  // và popup đổi mật khẩu (pu8) bị chặn vĩnh viễn.
  if (typeUser === undefined || typeUser === null || typeUser === "") return;
  if (!dispatch) return;

  localStorage.setItem(KEY_LOCAL_TYPE_USER, typeUser);

  const tourHistoryKey = `TOUR_SHOWN_${phoneNumber}`;
  const tourHistory = readTourHistory(tourHistoryKey);
  const userTypeKey = String(typeUser);

  const hasShownTourForThisUserType = !!tourHistory[userTypeKey];
  // Ép số ở cả 2 vế: API có thể trả type_user dạng chuỗi, bản copy-paste cũ so
  // sánh trực tiếp nên chuỗi "1" sẽ rơi nhầm xuống nhánh cuối và tắt tour.
  const typeUserNumber = +typeUser;
  const isKnownType = Object.values(TYPE_USER).includes(typeUserNumber);
  const isPU4EligibleType = PU4_ELIGIBLE_TYPES.includes(typeUserNumber);

  // Lần đầu gặp nhóm user này trên số máy này → mở tour và ghi nhớ đã mời.
  if (
    isKnownType &&
    typeUserNumber !== TYPE_USER.general &&
    !hasShownTourForThisUserType
  ) {
    dispatch(setReduxDataTypeIntroTour({ typeActive: typeUserNumber, step: 0 }));
    tourHistory[userTypeKey] = true;
    localStorage.setItem(tourHistoryKey, JSON.stringify(tourHistory));
    return;
  }

  // Đã mời rồi nhưng thuộc nhóm được lặp lại — đặt lại về màn mời, tần suất
  // (1 lần / 7 ngày sau khi đóng) do isTourInviteDue quyết định.
  if (isPU4EligibleType) {
    dispatch(setReduxDataTypeIntroTour({ typeActive: typeUserNumber, step: 0 }));
    return;
  }

  // Không thuộc diện onboarding → đánh dấu tour đã xong để các popup khác
  // (vd pu8 đổi mật khẩu) không bị chặn bởi điều kiện "tour chưa hoàn tất".
  localStorage.setItem(KEY_LOCAL_TOUR_COMPLETE, "true");
  dispatch(
    setReduxDataTypeIntroTour({ typeActive: TYPE_USER.general, step: -1 }),
  );
}
